/* eslint-disable */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Typography, Box, Slide, useTheme, Button } from '@mui/material';
import { withTransaction } from '@elastic/apm-rum-react';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { useNavigate } from 'react-router-dom';
import ReactMuiTableListView from '../../components/Common/TableComponent/TableComponent';
import ReactMuiTableColumnHeaderTextEllipsis from '../../components/Common/ReactMuiTableColumnHeaderTextEllipsis';
import SearchBox from '../../components/Common/SearchBox';
import { ReactComponent as NoDataImage } from '../../assets/NoDataImage.svg';
import { ReactComponent as NoDataCreated } from '../../assets/NoDataCreated.svg';
import { ReactComponent as UserMappingIcon } from '../../assets/UserMappingIcon.svg';
import useOrganization from '../../hooks/UseOrganization';
import DeleteDialog from '../../components/Common/DeleteDialog';
import ChangeStatusDialog from '../../components/Common/ChangeStatusDialog';
import IdentityProviderDialog from '../../components/IdentityProviderDialog';
import {
  CommonTableCell,
  StatusCell,
} from '../../components/Common/CommonTableCell';
import utils from '../../utils/Utils';

/**
 * @component ProvidersList
 * @description ProvidersList - Renders a component for managing identity providers.
 * @returns {JSX.Element} ProvidersList component.
 */
const ProvidersList = () => {
  const orgName = useSelector((state) => state.common.orgName);
  const theme = useTheme();
  const subadmin = useSelector((state) => state.common.subAdminBoolean);
  const navigate = useNavigate();

  const tableColumns = useMemo(
    () => [
      {
        heading: 'Name',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: 'display_name',
        Cell: CommonTableCell,
        width: 100,
        sortType: utils.caseInsensitiveSort,
      },
      {
        heading: 'Provider details',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: 'provider_id',
        Cell: CommonTableCell,
        width: 90,
      },
      {
        heading: 'Status',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: 'is_active',
        Cell: StatusCell,
        sortType: utils.statusSort,
        width: 100,
      },
      {
        heading: 'User Sync',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: 'sync',
        Cell: ({ value }) => (
          <CommonTableCell value={value ? 'Enabled' : 'Disabled'} />
        ),
        width: 80,
      },
    ],
    [utils]
  );

  const {
    getIdentityProviderList,
    createIdentityProviderSaml,
    changeStatusIdentityProvider,
    deleteIdentityProvider,
    updateIdentityProviderSaml,
    updateIdentityProviderOidc,
    createIdentityProviderOidc,
  } = useOrganization();

  const [searchTerm, setSearchTerm] = useState('');
  const [allIdentityProviderList, setAllIdentityProviderList] = useState([]); // All data from backend
  const [identityProviderList, setIdentityProviderList] = useState([]); // Filtered data for display
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQueryText, setSearchQueryText] = useState('');
  const [screenVisible, setScreenVisible] = useState('landing');
  const [showIdentityProviderDialog, setShowIdentityProviderDialog] =
    useState(false);
  const [mode, setMode] = useState('edit');
  const [currentIdentityProviderDetail, setCurrentIdentityProviderDetail] =
    useState({});
  const [modalConfig, setModalConfig] = useState({
    message: '',
    title: '',
    onChange: () => {},
  });

  const fetchIdentityProviderList = useCallback(() => {
    // Fetch all data without pagination parameters
    getIdentityProviderList(orgName).then((resp) => {
      const allData = resp?.data?.data || [];
      setAllIdentityProviderList(allData);
    });
  }, [orgName, getIdentityProviderList]);

  const handleUserSyncChange = useCallback(
    (orgName, currentIDP) => {
      if (currentIDP?.provider_id === 'SAML') {
        updateIdentityProviderSaml(orgName, {
          sync: !currentIDP?.sync,
        }).then(() => {
          fetchIdentityProviderList();
          setShowStatusModal(false);
        });
      } else {
        updateIdentityProviderOidc(orgName, {
          sync: !currentIDP?.sync,
        }).then(() => {
          fetchIdentityProviderList();
          setShowStatusModal(false);
        });
      }
    },
    [
      updateIdentityProviderSaml,
      updateIdentityProviderOidc,
      fetchIdentityProviderList,
      setShowStatusModal,
    ]
  );

  const handleStatusChange = useCallback(
    (orgName, currentIDP) => {
      if (currentIDP?.provider_id === 'SAML') {
        updateIdentityProviderSaml(orgName, {
          is_active: !currentIDP?.is_active,
        }).then(() => {
          fetchIdentityProviderList();
          setShowStatusModal(false);
        });
      } else {
        updateIdentityProviderOidc(orgName, {
          is_active: !currentIDP?.is_active,
        }).then(() => {
          fetchIdentityProviderList();
          setShowStatusModal(false);
        });
      }
    },
    [
      updateIdentityProviderSaml,
      updateIdentityProviderOidc,
      fetchIdentityProviderList,
      setShowStatusModal,
    ]
  );

  // Frontend filtering logic - filter data based on search term
  const { filteredData, totalCount: filteredTotalCount } = useMemo(() => {
    const filtered = allIdentityProviderList.filter((item) => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        item.display_name?.toLowerCase().includes(searchLower) ||
        item.provider_id?.toLowerCase().includes(searchLower) ||
        (item.is_active ? 'active' : 'inactive').includes(searchLower)
      );
    });

    return {
      filteredData: filtered,
      totalCount: filtered.length,
    };
  }, [allIdentityProviderList, searchTerm]);

  // Update display data when filtered data changes
  useEffect(() => {
    setIdentityProviderList(filteredData);
    setTotalCount(filteredTotalCount);
  }, [filteredData, filteredTotalCount]);

  const openDialogMapper = (providerId) => {
    setShowIdentityProviderDialog(true);
  };

  const handleCreateIdentityProvider = useCallback(() => {
    setMode('create');
    setCurrentIdentityProviderDetail({});
    setShowIdentityProviderDialog(true);
  }, []);

  // Function to open modal dynamically
  const openStatusModal = (idpDetail, actionType) => {
    if (actionType === 'changeStatus') {
      setModalConfig({
        message: `Are you sure you want to change status of "${idpDetail.display_name}" to "${idpDetail.is_active ? 'Inactive' : 'Active'}"?`,
        onChange: () => handleStatusChange(orgName, idpDetail),
        title: 'Change Status',
      });
    } else if (actionType === 'sync') {
      setModalConfig({
        message: `Are you sure you want to "${idpDetail.sync ? 'Disable' : 'Enable'}" user sync for "${idpDetail.display_name}"?`,
        onChange: () => handleUserSyncChange(orgName, idpDetail),
        title: 'Change User Sync Action',
      });
    }
    setShowStatusModal(true);
  };

  const menus = useMemo(
    () => [
      {
        label: 'Edit',
        id: 'edit',
        icon: EditIcon,
        onClick: ({ props }) => {
          setCurrentIdentityProviderDetail(props?.original);
          openDialogMapper(props?.original?.provider_id);
          setMode('edit');
          setScreenVisible('dialog');
        },
      },
      {
        label: 'Change Status',
        id: 'disable',
        icon: ChangeCircleIcon,
        isMenuDisabled: () => subadmin,
        onClick: ({ props }) => {
          setCurrentIdentityProviderDetail(props?.original);
          openStatusModal(props?.original, 'changeStatus'); // dynamic
        },
      },
      {
        label: 'Sync Actions',
        id: 'sync',
        icon: SyncAltIcon,
        onClick: ({ props }) => {
          setCurrentIdentityProviderDetail(props?.original);
          openStatusModal(props?.original, 'sync'); // dynamic
        },
      },
      {
        label: 'User Mapping',
        id: 'user-mapping',
        icon: UserMappingIcon,
        onClick: ({ props }) => {
          navigate(
            `/idp-setting/${props?.original?.provider_id}_${props?.original?.id}`,
            {
              state: {
                originalOrganization: orgName,
              },
            }
          );
        },
      },
      {
        label: 'Delete',
        id: 'delete',
        icon: DeleteIcon,
        onClick: ({ props }) => {
          setCurrentIdentityProviderDetail(props?.original);
          setShowDeleteModal(true);
        },
      },
    ],
    [orgName, subadmin, openStatusModal]
  );

  useEffect(() => {
    fetchIdentityProviderList();
  }, [screenVisible]);

  // Fetch data when orgName changes
  useEffect(() => {
    fetchIdentityProviderList();
  }, [orgName]);

  /**
   * @function debouncedSave
   * @description debounce function to update the search term
   * @returns {void} sets the the searchTerm value
   * @example debouncedSave()
   */
  const debouncedSave = useCallback(
    debounce((data) => {
      setSearchTerm(data);
    }, 800),
    []
  );

  /**
   * @function searchWebHookByEvent
   * @description searchWebHookByEvent - Callback function to search webhooks by event.
   * @param {Event} e - The event object.
   */
  const searchWebHookByEvent = useCallback(
    (e) => {
      const { value } = e.target;
      setSearchQueryText(value);
      debouncedSave(value);
    },
    [debouncedSave]
  );

  /**
   * @function handleSetSearchText
   * @description handles the input field of searchText.
   * @param {object} searchText - the current text is passed from user for searching.
   * @returns {void} sets the searchText value
   * @example handleSetSearchText(searchText)
   */
  const handleSetSearchText = (searchText) => {
    setSearchQueryText(searchText);
  };

  /**
   * @function handleDeleteIdentityProvider
   * @description handleDeleteIdentityProvider - Callback function to handle the deletion of a Identity Provider.
   * @param {number} id - The ID of the Identity Provider to be deleted.
   * @param {string} orgName - The ID of the organization to which the Identity Provider belongs.
   */
  const handleDeleteIdentityProvider = useCallback(
    (id, currentIdentityProvider) => {
      deleteIdentityProvider(id, {
        idp_id: currentIdentityProvider?.id,
        provider: currentIdentityProvider?.provider_id.toLowerCase(),
      }).then(() => {
        fetchIdentityProviderList();
      });
      setShowDeleteModal(false);
    },
    [deleteIdentityProvider, fetchIdentityProviderList]
  );

  /**
   * @function handleChangeStatusIdentityProvider
   * @description handleDeleteIdentityProvider - Callback function to handle the deletion of a Identity Provider.
   * @param {number} id - The ID of the Identity Provider to be deleted.
   * @param {string} orgName - The ID of the organization to which the Identity Provider belongs.
   */
  const handleChangeStatusIdentityProvider = useCallback(
    (id, payload) => {
      changeStatusIdentityProvider(id, payload).then(() => {
        fetchIdentityProviderList();
      });
      setShowDeleteModal(false);
    },
    [fetchIdentityProviderList, changeStatusIdentityProvider]
  );

  /**
   * @function onTableStateChangeMethod
   * @description helps in setting the pageIndex and pageSize
   * @param {object} pageIndex - pageIndex method of the table.
   * @returns {void} - updates the pageIndex and pageSize.
   * @example onTableStateChangeMethod(pageIndex)
   */
  const onTableStateChangeMethod = useCallback(
    (pageIndexData) => {
      setPageIndex(pageIndexData?.pageIndex);
      setPageSize(pageIndexData?.pageSize);
    },
    [setPageIndex, setPageSize]
  );

  const handleCloseIDPDialog = useCallback(() => {
    setShowIdentityProviderDialog(false);
  }, []);

  return (
    <>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        pt={1}
      >
        <Box width="200px">
          <SearchBox
            value={searchQueryText}
            onChange={searchWebHookByEvent}
            placeholder="Search providers"
          />
        </Box>
        <Box display="block">
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateIdentityProvider}
          >
            + Create Identity Provider
          </Button>
        </Box>
      </Box>
      <Box flexGrow={1} display="flex" flexDirection="column">
        <Slide in={true} direction="up" mountOnEnter timeout={500}>
          {identityProviderList?.length > 0 ? (
            <Box pt={1}>
              <ReactMuiTableListView
                data={filteredData} // Pass filtered data, let table handle pagination
                columns={tableColumns}
                onTableStateChange={onTableStateChangeMethod}
                getHeaderProps={() => ({
                  style: {
                    display: 'flex',
                    alignItems: 'center',
                  },
                })}
                getRowProps={() => ({
                  style: {
                    position: 'relative',
                  },
                })}
                enableRowSelection={true}
                pageCount={Math.ceil(filteredTotalCount / 5)} // Calculate based on default page size
                dataLength={Number(filteredTotalCount)}
                enablePagination={true}
                initialPageSize={5}
                rowsPerPageOptions={[5, 10, 15, 20]}
                rowLevelRightClickMenus={menus}
                enableManualFilter={false} // Frontend filtering
                enableControlledPagination={false} // Let table handle pagination internally
                showPaginationAction={false}
                skipPageReset={false}
                searchTerm={searchTerm}
              />
            </Box>
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              overflow="auto"
              minHeight="300px"
              height="calc(100vh - 438px)"
            >
              {searchTerm ? (
                <NoDataImage style={{ color: theme.palette.primary.main }} />
              ) : (
                <NoDataCreated style={{ color: theme.palette.primary.main }} />
              )}
              <Typography
                variant="subtitle1"
                textAlign="center"
                sx={{ pt: '10px' }}
              >
                {searchTerm
                  ? 'No matching result found.'
                  : 'No providers found. Please create a provider to proceed.'}
              </Typography>
            </Box>
          )}
        </Slide>
      </Box>
      <ChangeStatusDialog
        open={showStatusModal}
        onCloseClick={() => setShowStatusModal(false)}
        onChangeClick={modalConfig.onChange}
        confirmationMsg={modalConfig.message}
        dialogTitle={modalConfig.title}
      />
      <DeleteDialog
        open={showDeleteModal}
        onCloseClick={() => setShowDeleteModal(false)}
        onDeleteClick={() =>
          handleDeleteIdentityProvider(orgName, currentIdentityProviderDetail)
        }
        confirmationMsg={`Are you sure you want to delete "${currentIdentityProviderDetail?.display_name}"?`}
        resourceName="Identity Provider"
      />

      <IdentityProviderDialog
        open={showIdentityProviderDialog}
        mode={mode}
        currentIdentityProviderDetail={currentIdentityProviderDetail}
        organizationID={orgName}
        onClose={handleCloseIDPDialog}
        createIdentityProviderOidc={createIdentityProviderOidc}
        updateIdentityProviderOidc={updateIdentityProviderOidc}
        createIdentityProviderSaml={createIdentityProviderSaml}
        updateIdentityProviderSaml={updateIdentityProviderSaml}
        onSuccess={fetchIdentityProviderList}
      />
    </>
  );
};

export default withTransaction('ProvidersList', 'component')(ProvidersList);
